/* eslint-disable react/prop-types */
import React from 'react';
import { Avatar, Button, Card, Divider, Image, Space } from 'antd';

import { guuid } from '@/utils/utils';

/**
 * 单条 RPA 微信消息的渲染（原 `ChatListView` 里的 `chartContentView`）
 * ==================================================================
 * 抽出来的原因：这条渲染被两处共用，各留一份必然漂移 ——
 *   1. 会话记录页（`/rpaMessageCollection/queryMessageCollectionList`）；
 *   2. 商家洞察 · 教练明细「查看证据」弹窗里的上下文消息
 *      （`/rpaMessageCollection/queryWeChatMessageAroundMessageId`）。
 *
 * ⚠ `messageType` 是**开放集合**（服务端枚举还在加），未知值走最后一支兜底显示
 *   枚举文案；**不要**改成会静默吞掉新取值的 switch。
 * ⚠ `payload` 由服务端解析过了（workwx `buildRpaMessageCollection` 把 JSON 串转成了对象），
 *   这里直接按对象取字段，不要再 `JSON.parse`。
 *
 * @param record          一条 `RpaMessageCollectionModel`
 * @param onSaveMaterial  可选。只有传了才渲染视频号卡片右下角的「保存至素材」按钮：
 *                        会话记录页要用它弹保存弹窗；证据上下文是只读查看，不传。
 */

/** 联系人类型 → 中文（`contactType === 1` 时 @ 后缀用绿色，其余橙色） */
const CONTACT_TYPE_TEXT = {
  0: '未知联系人',
  1: '微信',
  2: '公众号',
  3: 'LITTA',
};

/** 消息类型 → 兜底文案（只用于上面那些分支都没命中的情况） */
const MESSAGE_TYPE_TEXT = {
  0: '未知',
  1: '文件',
  2: '语音',
  3: '名片',
  4: '聊天历史',
  5: '表情',
  6: '图片',
  7: '文字',
  8: '位置',
  9: '小程序',
  10: '钱相关',
  11: '撤回消息',
  12: '图文消息',
  13: '视频',
  9999: '入群邀请',
  10000: '秒回系统消息',
  10001: '企业微信系统消息',
};

const ChatMessageItem = (props) => {
  const { record, onSaveMaterial } = props;
  return (
    <div style={{ display: 'flex', alignItems: 'start' }}>
      <div>
        <Avatar
          shape="square"
          size={50}
          src={record?.avatar}
          style={{ marginTop: 5 }}
        />
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          marginLeft: 10,
        }}
      >
        <Space size={10}>
          <span>{record?.contactName}</span>
          <span
            style={{
              color: record?.contactType === 1 ? '#557822' : '#e89e42',
            }}
          >
            {' @' + CONTACT_TYPE_TEXT[record?.contactType]}
          </span>
          <span style={{ color: '#aaaaaa' }}>{record?.messageTimeInfo}</span>
        </Space>
        <div style={{ display: 'flex', maxWidth: '50%', marginTop: 10 }}>
          {record?.messageType === 6 ? (
            // 图片
            <Image
              width={150}
              key={guuid()}
              src={
                record?.payload?.sourceImage?.url || record?.payload?.imageUrl
              }
              style={{ borderRadius: 10 }}
            />
          ) : record?.messageType === 14 ? (
            // 视频号
            <Card bodyStyle={{ padding: 10, width: 220 }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar
                    shape="square"
                    size={16}
                    src={record?.payload?.avatarUrl}
                  />
                  <span
                    style={{ color: '#8c8c8c', fontSize: 12, marginLeft: 2 }}
                  >
                    {record?.payload?.nickname}
                  </span>
                </div>
                <span style={{ marginTop: 5 }}>
                  {record?.payload?.description}
                </span>
                <Image
                  width={200}
                  key={guuid()}
                  src={record?.payload?.coverUrl}
                  style={{ borderRadius: 10, marginTop: 2 }}
                />
                <Divider
                  style={{
                    height: '0.5',
                    background: '#e8e8e8',
                    marginTop: 5,
                    marginBottom: 5,
                  }}
                />
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span
                    style={{ color: '#8c8c8c', fontSize: 10, marginLeft: 2 }}
                  >
                    视频号
                  </span>

                  {onSaveMaterial ? (
                    <Button
                      type="link"
                      onClick={() => onSaveMaterial(record?.payload || {})}
                    >
                      保存至素材
                    </Button>
                  ) : null}
                </div>
              </div>
            </Card>
          ) : record?.messageType === 9 ? (
            // 小程序
            <Card bodyStyle={{ padding: 10, width: 220 }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar
                    shape="square"
                    size={16}
                    src={record?.payload?.iconUrl}
                  />
                  <span
                    style={{ color: '#8c8c8c', fontSize: 12, marginLeft: 2 }}
                  >
                    {record?.payload?.title}
                  </span>
                </div>
                <span style={{ marginTop: 5 }}>
                  {record?.payload?.description}
                </span>
                <Image
                  width={200}
                  key={guuid()}
                  src={record?.payload?.thumbUrl}
                  style={{ borderRadius: 10, marginTop: 2 }}
                />
                <Divider
                  style={{
                    height: '0.5',
                    background: '#e8e8e8',
                    marginTop: 5,
                    marginBottom: 5,
                  }}
                />
                <span style={{ color: '#8c8c8c', fontSize: 10, marginLeft: 2 }}>
                  小程序
                </span>
              </div>
            </Card>
          ) : record?.messageType === 12 ? (
            // 图文卡片
            <Card bodyStyle={{ padding: 12, width: 280 }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                }}
              >
                {/* 主要内容区域 - 横向布局 */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 12,
                  }}
                >
                  {/* 左侧标题 */}
                  <div
                    style={{
                      flex: 1,
                      fontSize: 14,
                      color: '#333',
                      lineHeight: '20px',
                      marginRight: 12,
                    }}
                  >
                    {record?.payload?.title}
                  </div>
                  {/* 右侧图片 */}
                  <img
                    src={record?.payload?.thumbnailUrl}
                    style={{
                      width: 60,
                      height: 60,
                      borderRadius: 8,
                      objectFit: 'cover',
                    }}
                  />
                </div>
                {/* 底部标识 */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      backgroundColor: '#1890ff',
                      borderRadius: 2,
                      marginRight: 6,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 10,
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                  >
                    善
                  </div>
                  <div
                    style={{
                      color: '#666',
                      fontSize: 12,
                    }}
                  >
                    图文卡片
                  </div>
                </div>
              </div>
            </Card>
          ) : record?.messageType === 3 ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                padding: '12px',
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                border: '1px solid #e8e8e8',
                minWidth: '120px',
                maxWidth: '200px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: 8,
                }}
              >
                <Avatar
                  src={record?.payload?.avatar}
                  size={32}
                  style={{ marginRight: 8 }}
                >
                  {record?.payload?.name?.charAt(0) || '名'}
                </Avatar>
                <div
                  style={{
                    fontSize: 14,
                    color: '#333',
                    fontWeight: 500,
                    wordBreak: 'break-all',
                    lineHeight: '1.2',
                    flex: 1,
                  }}
                >
                  {record?.payload?.name || '未知用户'}
                </div>
              </div>
              <div
                style={{
                  width: '100%',
                  height: '1px',
                  backgroundColor: '#e8e8e8',
                  marginBottom: 8,
                }}
              ></div>
              <div
                style={{
                  fontSize: 12,
                  color: '#999',
                  textAlign: 'left',
                  width: '100%',
                }}
              >
                个人名片
              </div>
            </div>
          ) : record?.messageType === 4 ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                padding: '12px',
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                border: '1px solid #d9d9d9',
                maxWidth: '280px',
                minWidth: '200px',
              }}
            >
              {/* 标题 */}
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: '#333',
                  marginBottom: '8px',
                  lineHeight: '1.4',
                }}
              >
                {record?.payload?.content || '群聊的聊天记录'}
              </div>
              {/* 聊天明细 */}
              <div
                style={{
                  marginBottom: '8px',
                }}
              >
                {record?.payload?.chatHistoryList &&
                record.payload.chatHistoryList.length > 0 ? (
                  record.payload.chatHistoryList
                    .slice(0, 4)
                    .map((chat, index) => (
                      <div
                        key={index}
                        style={{
                          fontSize: 12,
                          color: '#666',
                          lineHeight: '1.5',
                          marginBottom: '2px',
                          wordBreak: 'break-all',
                        }}
                      >
                        <span style={{ color: '#333', fontWeight: 500 }}>
                          {chat.senderName}
                        </span>
                        <span>: </span>
                        <span>
                          {(() => {
                            const message =
                              typeof chat.message === 'string'
                                ? chat.message
                                : chat.message?.content ||
                                  JSON.stringify(chat.message) ||
                                  '消息内容';
                            return message && message.length > 20
                              ? message.substring(0, 20) + '...'
                              : message;
                          })()}
                        </span>
                      </div>
                    ))
                ) : (
                  <div
                    style={{
                      fontSize: 12,
                      color: '#999',
                      fontStyle: 'italic',
                    }}
                  >
                    暂无聊天记录
                  </div>
                )}
              </div>

              {/* 分隔线 */}
              <div
                style={{
                  height: '1px',
                  backgroundColor: '#e8e8e8',
                  margin: '4px 0',
                }}
              ></div>

              {/* 底部标识 */}
              <div
                style={{
                  fontSize: 12,
                  color: '#999',
                  textAlign: 'left',
                  marginTop: '4px',
                }}
              >
                聊天记录
              </div>
            </div>
          ) : record?.messageType === 8 ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                padding: '8px',
                backgroundColor: '#f5f5f5',
                borderRadius: '6px',
                border: '1px solid #d9d9d9',
                maxWidth: '200px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '4px',
                }}
              >
                <span
                  style={{ marginRight: 6, fontSize: 16, color: '#52c41a' }}
                >
                  📍
                </span>
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: '#333',
                  }}
                >
                  {record?.payload?.name || '位置'}
                </span>
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: '#666',
                  lineHeight: '1.4',
                  paddingLeft: '22px',
                }}
              >
                {record?.payload?.address || '地址信息'}
              </div>
            </div>
          ) : (
            // 其他
            <span
              style={{
                borderRadius: 10,
                background: '#d7d7d7',
                paddingLeft: 5,
                paddingRight: 5,
                paddingTop: 10,
                paddingBottom: 10,
                whiteSpace: 'pre-wrap',
              }}
            >
              {record?.messageType === 7 ? (
                record?.payload?.text
              ) : record?.messageType === 5 ? (
                <img
                  src={record?.payload?.imageUrl}
                  alt="表情"
                  style={{
                    maxWidth: '60px',
                    maxHeight: '60px',
                    objectFit: 'contain',
                  }}
                />
              ) : record?.messageType === 2 ? (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span
                    style={{ marginRight: 6, fontSize: 16, color: '#ff4d4f' }}
                  >
                    🎙️
                  </span>
                  <span>{record?.payload?.text || '语音消息'}</span>
                </div>
              ) : record?.messageType === 1 ? (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span
                    style={{ marginRight: 6, fontSize: 16, color: '#1890ff' }}
                  >
                    📄
                  </span>
                  <span>{record?.payload?.name || '文件'}</span>
                </div>
              ) : (
                MESSAGE_TYPE_TEXT[record?.messageType]
              )}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatMessageItem;
